import React, { useState, useEffect, useCallback, useRef } from 'react'
import { fetchClients, fetchAccommodations, fetchStaysForPitch, fetchLastPitchInventory, saveStaffInventory, saveContribution, createIntervention, fetchStaffUsers, restockInSeason, recordActivity, fetchInventoryDraft, uploadDraftFiles, saveInventoryDraft } from './fieldApi'
import { findAccommodation, modelThemes } from './fieldModel'
import { buildReference, nextTheme, plural, untouchedThemes } from './fieldReference'
import { nowSeconds, formatShortDate, formatDateTime } from './fieldDates'
import { isCurrentStay, guestName } from './fieldStays'
import { NoResultIcon } from './FieldIcons'
import { Notice, LinkButton, LoadingText } from './FieldUI'
import { buildStaffInventory } from './fieldSave'
import { summarize } from './fieldSummary'
import { draftState, restoreDraft } from './fieldDraft'
import { inventoryEntry, inventorySnapshot, interventionEntry, commentEntry, restoredCounts } from './fieldActivity'
import { kindTitle } from './fieldInterventions'
import FieldThemes, { CloseButton } from './FieldThemes'
import FieldThemeDetail from './FieldThemeDetail'
import FieldNoModel from './FieldNoModel'
import FieldReview from './FieldReview'
import FieldFollowUp from './FieldFollowUp'
import { missingObjects, restockAvailable } from './fieldInterventions'
import { emptyNotes, hasNotes, duringPhotoItems, afterPhotoItems, newComment, withComment, withPhotos, withPhoto, withoutPhotos, themeCommentItems, generalComment, withGeneralComment, themePhotos, commentChoices } from './fieldNotes'

// Remplissage d'un inventaire : modèle de l'hébergement, référence de comparaison,
// comptages thème par thème, commentaires et photos en volets (lot 3, décision 48), puis
// récapitulatif (W10c), enregistrement et Suite à donner (lot 4). Un échec garde toute la saisie.

// Modèles d'hébergement gardés le temps de la session
let accommodationsCache = null

// Numéro des interventions signalées avant l'enregistrement (pas encore créées sur le serveur)
let pendingSequence = 0

// context.type « tour » : inventaire d'un tour de terrain, enregistré hors séjour avec tour_id (décision 4).
// Une fois enregistré, onTourSaved({ payload, gaps, interventions }) (promesse) ramène à la fiche de l'emplacement
// (décision 50), qui tient lieu de Suite à donner.
// Pour un inventaire du tour, « Quitter » propose « Enregistrer pour plus tard » : la saisie est mise de côté sur le serveur
// (statut pending, hors de la liste des inventaires) et reprise à l'ouverture suivante de l'emplacement (fieldDraft.js).
// context.edit : { snapshot, inventoryId } pour modifier un inventaire rouvert depuis Mon activité (nouvelle version)
export default function FieldInventory({ context, user, onClose, onTourSaved, onActivity, scrollToTop })
{
	const { type, pitch, stay, client, tour } = context
	const recordType = type === 'tour' ? 'free' : type
	const [status, setStatus] = useState('loading')
	const [themes, setThemes] = useState([])
	const [reference, setReference] = useState(null)
	const [counts, setCounts] = useState({})
	const [doneThemes, setDoneThemes] = useState({})
	const [notes, setNotes] = useState(emptyNotes())
	const [screen, setScreen] = useState({ name: 'themes', themeId: null })
	const [accommodationId, setAccommodationId] = useState(null)
	const [saving, setSaving] = useState(false)
	const [saveError, setSaveError] = useState(false)
	const [saved, setSaved] = useState(null)
	const [toast, setToast] = useState(null)
	const [undoError, setUndoError] = useState(false)
	const [interventions, setInterventions] = useState([])
	const [currentStay, setCurrentStay] = useState(null)
	const [activityId, setActivityId] = useState(null)
	const [restored, setRestored] = useState(null)
	const [draftSaving, setDraftSaving] = useState(false)
	const [draftError, setDraftError] = useState(false)
	const editSnapshot = context.edit ? context.edit.snapshot : null
	const version = editSnapshot ? (editSnapshot.version || 1) + 1 : 1
	const [now] = useState(nowSeconds())
	const mounted = useRef(true)
	const notesRef = useRef(notes)
	notesRef.current = notes

	useEffect(() => () => {
		mounted.current = false
	}, [])

	const tourId = tour ? tour._id : null

	// Saisie mise de côté plus tôt pour cet emplacement dans le tour : reprise là où elle s'était arrêtée. Sans réseau ou sans
	// brouillon, l'inventaire démarre à vide.
	const loadDraft = useCallback((themesList) => {
		if (!tourId || editSnapshot)
			return Promise.resolve(null)
		return fetchInventoryDraft(tourId, pitch.location)
			.then(draft => {
				if (!draft || !draft.state)
					return null
				const back = restoreDraft(themesList, draft.state)
				setCounts(back.counts)
				setDoneThemes(back.doneThemes)
				setNotes(back.notes)
				setInterventions(back.interventions)
				setRestored({ time: draft.update_time, name: draft.state.user_name || '' })
				return draft
			})
			.catch(() => null)
	}, [tourId, pitch, editSnapshot])

	const load = useCallback(() => {
		setStatus('loading')
		const accommodations = accommodationsCache ? Promise.resolve(accommodationsCache) : fetchAccommodations()
		Promise.all([accommodations, fetchStaysForPitch(pitch.location).catch(() => [])])
			.then(([list, stays]) => {
				accommodationsCache = list
				const category = pitch.location_category || (stay && stay.location_category) || ''
				const accommodation = findAccommodation(list, category)
				const modelThemesList = modelThemes(accommodation)
				setAccommodationId(accommodation ? accommodation._id || null : null)
				if (modelThemesList.length === 0)
				{
					setThemes([])
					return loadDraft([]).then(() => {
						setStatus('no-model')
						return null
					})
				}
				// Hors séjour et tour : un séjour en cours s'affiche en simple information (décisions 3 et 4)
				const ongoing = !stay ? stays.find(item => item.status !== 'canceled' && isCurrentStay(item, now)) : null
				if (ongoing)
					fetchClients([ongoing]).then(byNumber => {
						if (mounted.current)
							setCurrentStay({ stay: ongoing, name: guestName(byNumber[ongoing.client_number]) })
					})
				return fetchLastPitchInventory(pitch.location)
					.then(last => {
						setThemes(modelThemesList)
						setReference(buildReference(modelThemesList, last))
						if (editSnapshot)
						{
							// Modifier depuis Mon activité : comptages et commentaires repris, thèmes faits
							const allDone = {}
							modelThemesList.forEach(theme => {
								allDone[theme.id] = true
							})
							setCounts(restoredCounts(modelThemesList, editSnapshot))
							setDoneThemes(allDone)
							setNotes(editSnapshot.comments.reduce((acc, comment) => {
								const theme = modelThemesList.find(item => item.name === comment.theme)
								return withComment(acc, { ...newComment({ themeId: theme ? theme.id : null, text: comment.text, user: null, now: comment.time, after: false }), commentUser: comment.user })
							}, emptyNotes()))
						}
						return loadDraft(modelThemesList).then(() => setStatus('ready'))
					})
			})
			.catch(() => setStatus('error'))
	}, [pitch, stay, now, editSnapshot, loadDraft])

	useEffect(() => {
		load()
	}, [load])

	useEffect(() => {
		if (scrollToTop)
			scrollToTop()
	}, [screen, status, saved, scrollToTop])

	const dirty = Object.keys(counts).length > 0 || Object.keys(doneThemes).length > 0 || hasNotes(notes) || interventions.length > 0

	// Interventions signalées pendant l'inventaire : créées une fois l'inventaire enregistré, avec son
	// inventory_id (et le tour, s'il y en a un). Renvoie celles qui sont créées et le nombre d'échecs ; les
	// échecs sont à recréer depuis Suite à donner.
	const createPendingInterventions = (inventory, inventoryEntryId, pending, tourId) => {
		if (pending.length === 0)
			return Promise.resolve({ created: [], failed: 0 })
		return Promise.all(pending.map(item => createIntervention({ ...item.doc, inventory_id: inventory._id, tour_id: tourId }, item.photos)
			.then(created => ({ item, created }))
			.catch(() => ({ item, created: null }))))
			.then(results => {
				const done = results.filter(result => !!result.created)
				done.forEach(result => recordActivity(interventionEntry({ doc: result.created, kindTitle: kindTitle(result.item.kind), pitch, user, time: nowSeconds(), inventoryEntryId })))
				return {
					created: done.map(result => ({ id: result.created._id, kind: result.item.kind, doc: result.created, photoCount: result.item.photoCount, during: true })),
					failed: results.length - done.length
				}
			})
	}

	const save = () => {
		if (saving)
			return ;
		const ready = status === 'ready'
		const payload = { ...buildStaffInventory({ type: recordType, pitch, stay, accommodationId, themes: ready ? themes : [], counts, reference: ready ? reference : null, notes, user, now: nowSeconds() }), tour_id: tour ? tour._id : null, version, inventory_id: context.edit ? context.edit.inventoryId || null : null }
		const pending = interventions.filter(item => item.pending)
		setSaving(true)
		setSaveError(false)
		// Inventaire d'un tour : l'enregistrement part avec les actions du tour (onTourSaved), qui le met en file d'attente
		// sur le téléphone sans réseau ; les interventions signalées pendant l'inventaire partent avec lui.
		if (tour && onTourSaved)
		{
			const time = nowSeconds()
			const summary = ready ? summarize(type, themes, counts, reference, time) : null
			const snapshot = inventorySnapshot({ type, pitch, stay, client, themes: ready ? themes : [], counts, reference: ready ? reference : null, notes, summary, interventions, user, time, version })
			const entry = inventoryEntry({ type, pitch, tour, user, time, version, inventoryId: null, summary, snapshot })
			const created = pending.map(item => ({ kind: item.kind, doc: { ...item.doc, tour_id: tour._id, client_ref: 'tour-' + tour._id + '-' + time + '-' + Math.random().toString(36).slice(2, 8) }, photos: item.photos || [] }))
			return onTourSaved({ payload, gaps: summary ? summary.gaps.length : 0, interventions: created })
				.then(() => {
					recordActivity(entry)
					created.forEach(item => recordActivity(interventionEntry({ doc: item.doc, kindTitle: kindTitle(item.kind), pitch, user, time: nowSeconds(), inventoryEntryId: entry.id })))
				})
				.catch(() => {
					if (!mounted.current)
						return ;
					setSaving(false)
					setSaveError(true)
				})
		}
		saveStaffInventory(payload)
			.then(result => {
				if (!mounted.current)
					return ;
				const time = nowSeconds()
				const summary = ready ? summarize(type, themes, counts, reference, time) : null
				const snapshot = inventorySnapshot({ type, pitch, stay, client, themes: ready ? themes : [], counts, reference: ready ? reference : null, notes, summary, interventions, user, time, version })
				const entry = inventoryEntry({ type, pitch, tour, user, time, version, inventoryId: result._id, summary, snapshot })
				recordActivity(entry)
				setSaving(false)
				setSaved(result)
				setActivityId(entry.id)
				return createPendingInterventions(result, entry.id, pending, null)
					.then(({ created, failed }) => {
						if (!mounted.current)
							return ;
						setInterventions(current => current.filter(item => !item.pending).concat(created))
						if (failed > 0)
							setToast({ text: plural(failed, 'intervention non créée', 'interventions non créées') + ' : recréez-la depuis Suite à donner.' })
					})
			})
			.catch(() => {
				if (!mounted.current)
					return ;
				setSaving(false)
				setSaveError(true)
			})
	}

	// « Enregistrer pour plus tard » : photos et problèmes signalés partent sur le serveur avec le reste de la saisie, puis
	// l'écran se ferme. Sans réseau, rien n'est mis de côté et la saisie reste à l'écran (draftError).
	const saveForLater = () => {
		if (draftSaving || !tour)
			return ;
		setDraftSaving(true)
		setDraftError(false)
		uploadDraftFiles(notes, interventions.filter(item => item.pending))
			.then(uploaded => saveInventoryDraft(tour._id, pitch.location, user, draftState({ themes: status === 'ready' ? themes : [], counts, doneThemes, notes: uploaded.notes, interventions: uploaded.interventions, user })))
			.then(() => {
				if (mounted.current)
					onClose({ draft: true })
			})
			.catch(() => {
				if (!mounted.current)
					return ;
				setDraftSaving(false)
				setDraftError(true)
			})
	}

	const dismissToast = useCallback(() => setToast(null), [])

	// Avant l'enregistrement, commentaires et photos restent sur le téléphone. Après, chaque contribution
	// part tout de suite (décisions 14 et 31) : modifier ou supprimer s'applique à l'écran, puis est annulé
	// si l'envoi échoue. notesRef : une annulation depuis le bandeau doit partir de l'état actuel.
	const updateComment = (comment) => {
		const previous = notesRef.current.comments.find(item => item.id === comment.id)
		setNotes(current => withComment(current, comment))
		if (!saved)
			return Promise.resolve()
		return saveContribution(saved._id, 'update-comment', { comment }, user)
			.catch(error => {
				if (mounted.current && previous)
					setNotes(current => withComment(current, previous))
				throw error
			})
	}

	const undoFromToast = () => {
		const current = toast
		setToast(null)
		setUndoError(false)
		Promise.resolve(current.undo()).catch(() => {
			if (mounted.current)
				setUndoError(true)
		})
	}

	const addThemeComment = (themeId, text) => {
		setNotes(current => withComment(current, newComment({ themeId, text, user, now: nowSeconds(), after: false })))
	}

	const addThemePhotos = (themeId, photos) => {
		setNotes(current => withPhotos(current, photos, themeId, false))
	}

	const addCommentAfter = (text) => {
		const comment = newComment({ themeId: null, text, user, now: nowSeconds(), after: true })
		return saveContribution(saved._id, 'add-comment', { comment }, user)
			.then(() => {
				if (!mounted.current)
					return ;
				setNotes(current => withComment(current, comment))
				setUndoError(false)
				recordActivity(commentEntry({ pitch, type, user, time: comment.commentTime, inventoryEntryId: activityId, text }))
				setToast({ text: 'Commentaire ajouté', undo: () => updateComment({ ...comment, commentDeletedTime: nowSeconds() }) })
			})
	}

	const addPhotosAfter = (photos) => {
		const ids = photos.map(photo => photo.id)
		return saveContribution(saved._id, 'add-photos', { photos: photos.map(photo => ({ id: photo.id, file: photo.file, description: photo.description, user: photo.user, timestamp: photo.time })) }, user)
			.then(() => {
				if (!mounted.current)
					return ;
				setNotes(current => withPhotos(current, photos, null, true))
				setUndoError(false)
				setToast({
					text: plural(photos.length, 'photo ajoutée', 'photos ajoutées'),
					undo: () => {
						const previous = notesRef.current
						setNotes(current => withoutPhotos(current, ids))
						return saveContribution(saved._id, 'remove-photos', { ids }, user)
							.catch(error => {
								if (mounted.current)
									setNotes(previous)
								throw error
							})
					}
				})
			})
	}

	// Interventions (décisions 30, 42 et 43) : signalées pendant l'inventaire (during), elles sont créées à
	// l'enregistrement ; depuis Suite à donner, elles sont créées tout de suite. Toujours liées à
	// l'inventaire par inventory_id.
	const addIntervention = ({ kind, doc, photos }) => {
		if (!saved)
		{
			pendingSequence++
			setInterventions(current => current.concat([{ id: 'pending-' + pendingSequence, kind, doc, photos, photoCount: photos.length, during: true, pending: true }]))
			return Promise.resolve()
		}
		return createIntervention({ ...doc, inventory_id: saved._id, tour_id: tour ? tour._id : null }, photos)
			.then(created => {
				if (!mounted.current)
					return ;
				setInterventions(current => current.concat([{ id: created._id, kind, doc: created, photoCount: photos.length, during: false }]))
				recordActivity(interventionEntry({ doc: created, kindTitle: kindTitle(kind), pitch, user, time: nowSeconds(), inventoryEntryId: activityId }))
				setToast({ text: 'Intervention créée' })
			})
	}

	const setCount = (objectId, value) => setCounts(previous => ({ ...previous, [objectId]: value }))

	if (status === 'loading' || status === 'error')
	{
		return (
			<div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
				<div style={{ display: 'flex', alignItems: 'center', minHeight: 56, padding: '4px 20px 0' }}>
					<CloseButton onClick={onClose} />
				</div>
				{status === 'loading' && <LoadingText>Chargement de l'inventaire…</LoadingText>}
				{status === 'error' && (
					<Notice
						icon={<NoResultIcon />}
						title="Impossible de charger l'inventaire"
						hint="Vérifiez la connexion, puis réessayez."
						action={<div style={{ marginLeft: -12 }}><LinkButton onClick={load}>Réessayer</LinkButton></div>}
					/>
				)}
			</div>
		)
	}

	const missing = status === 'ready' ? missingObjects(themes, counts, reference) : []

	if (saved)
	{
		return (
			<FieldFollowUp
				type={type}
				pitch={pitch}
				stay={stay}
				client={client}
				saved={saved}
				themes={themes}
				notes={notes}
				user={user}
				now={now}
				toast={toast}
				undoError={undoError}
				onUndo={undoFromToast}
				onDismissToast={dismissToast}
				onAddComment={addCommentAfter}
				onUpdateComment={updateComment}
				onAddPhotos={addPhotosAfter}
				interventions={interventions}
				missing={missing}
				restockOffered={restockAvailable(missing, restockInSeason(), interventions)}
				loadUsers={fetchStaffUsers}
				onCreateIntervention={addIntervention}
				onFinish={onClose}
				onActivity={onActivity}
			/>
		)
	}

	if (screen.name === 'review' && status === 'ready')
	{
		return (
			<FieldReview
				type={type}
				pitch={pitch}
				stay={stay}
				client={client}
				themes={themes}
				counts={counts}
				reference={reference}
				now={now}
				saving={saving}
				saveError={saveError}
				onBack={() => setScreen({ name: 'themes', themeId: null })}
				onOpenTheme={(themeId) => setScreen({ name: 'theme', themeId })}
				onSave={save}
			/>
		)
	}

	if (status === 'no-model')
	{
		return (
			<FieldNoModel
				type={type}
				pitch={pitch}
				stay={stay}
				client={client}
				comment={(generalComment(notes) || { comment: '' }).comment}
				photos={themePhotos(notes, null)}
				user={user}
				onCommentChange={(text) => setNotes(current => withGeneralComment(current, text, user, nowSeconds()))}
				onPhotosChange={(photos) => setNotes(current => withPhotos({ ...current, photos: current.photos.filter(photo => photo.themeId !== null) }, photos, null, false))}
				saving={saving}
				saveError={saveError}
				onSave={save}
				onClose={onClose}
				onSaveLater={tour ? saveForLater : null}
				savingLater={draftSaving}
				saveLaterError={draftError}
				restored={!!restored}
			/>
		)
	}

	const themeIndex = themes.findIndex(theme => theme.id === screen.themeId)
	const theme = themeIndex >= 0 ? themes[themeIndex] : null

	if (screen.name === 'theme' && theme)
	{
		return (
			<FieldThemeDetail
				key={theme.id}
				type={type}
				pitch={pitch}
				theme={theme}
				index={themeIndex}
				total={themes.length}
				reference={reference}
				counts={counts}
				nextName={(nextTheme(themes, doneThemes, theme.id) || {}).name}
				commentItems={themeCommentItems(notes, theme.id)}
				photoItems={themePhotos(notes, theme.id).map(photo => ({ photo, themeName: null }))}
				inventoryPhotos={notes.photos}
				user={user}
				now={now}
				onCount={setCount}
				onFinish={() => {
					const next = nextTheme(themes, doneThemes, theme.id)
					setDoneThemes(previous => ({ ...previous, [theme.id]: true }))
					setScreen(next ? { name: 'theme', themeId: next.id } : { name: 'review', themeId: null })
				}}
				onAddComment={(text) => addThemeComment(theme.id, text)}
				onUpdateComment={updateComment}
				onAddPhotos={(photos) => addThemePhotos(theme.id, photos)}
				onUpdatePhoto={(photo) => setNotes(current => withPhoto(current, photo))}
				onRemovePhoto={(photo) => {
					URL.revokeObjectURL(photo.url)
					setNotes(current => withoutPhotos(current, [photo.id]))
				}}
				onBack={() => setScreen({ name: 'themes', themeId: null })}
			/>
		)
	}

	const draftNotice = restored ? { title: 'Inventaire repris', text: 'Mis de côté ' + formatDateTime(restored.time, now) + (restored.name ? ' par ' + restored.name : '') + ' : poursuivez là où il s\'était arrêté.' } : null

	return (
		<FieldThemes
			notice={draftNotice || (currentStay ? { title: 'Séjour en cours' + (currentStay.name ? ' : ' + currentStay.name : '') + ', jusqu\'au ' + formatShortDate(currentStay.stay.departure_time, now), text: tour ? 'Pour information : l\'inventaire du tour reste hors séjour.' : 'Pour information : l\'inventaire reste rattaché à l\'emplacement, pas au séjour.' } : null)}
			type={type}
			pitch={pitch}
			stay={stay}
			client={client}
			themes={themes}
			reference={reference}
			counts={counts}
			doneThemes={doneThemes}
			dirty={dirty}
			now={now}
			user={user}
			problems={interventions.length}
			inventoryPhotos={duringPhotoItems(themes, notes).concat(afterPhotoItems(notes))}
			inventoryComments={commentChoices(notes.comments, user)}
			loadUsers={fetchStaffUsers}
			onReportProblem={addIntervention}
			onOpenTheme={(themeId) => setScreen({ name: 'theme', themeId })}
			onCount={setCount}
			onConfirmAll={() => {
				// Seuls les thèmes que la personne n'a ni validés ni commencés sont complétés ; le récapitulatif
				// ne s'ouvre que si plus aucun thème n'est à vérifier
				const nextDone = { ...doneThemes }
				untouchedThemes(themes, counts, doneThemes).forEach(item => {
					nextDone[item.id] = true
				})
				setDoneThemes(nextDone)
				if (themes.every(item => nextDone[item.id]))
					setScreen({ name: 'review', themeId: null })
			}}
			onReview={() => setScreen({ name: 'review', themeId: null })}
			onClose={onClose}
			onSaveLater={tour ? saveForLater : null}
			savingLater={draftSaving}
			saveLaterError={draftError}
			restored={!!restored}
		/>
	)
}
